import {
  EVENT_CHANGE_STATUS_SUCCESS,
  EVENT_PATCH_SUCCESS,
  EVENT_SUCCESS,
} from '@pingum/app-state-management/event/types';
import { isEqual } from 'lodash';

import { generateEventsFromSchedules } from '../../utils/scheduleHelper';
import { SELECT_BARN } from '../barn/types';
import { getPendingCreatedSchedules } from './saveCatchUp';
import { matchesBoardItem } from './boardItemMatches';
import * as scheduleTypes from '../schedule/types';
import * as types from './types';

const collections = ['tasks', 'incompleteTasks', 'schedules', 'scheduleExceptions'];
const mutationTypes = new Set([
  EVENT_PATCH_SUCCESS,
  EVENT_CHANGE_STATUS_SUCCESS,
  EVENT_SUCCESS,
  types.BOARD_TASK_UPDATE,
  types.BOARD_SCHEDULE_ADDED_UPDATE,
  types.BOARD_SCHEDULE_CHANGED_UPDATE,
  types.BOARD_SCHEDULE_CANCELED_UPDATE,
  types.BOARD_SCHEDULE_EXCEPTION_ADDED_UPDATE,
  types.BOARD_SCHEDULE_EXCEPTION_CHANGED_UPDATE,
  types.BOARD_SCHEDULE_EXCEPTION_CANCELED_UPDATE,
  scheduleTypes.ADD_SCHEDULE_SUCCESS,
  scheduleTypes.UPDATE_SCHEDULE_SUCCESS,
  scheduleTypes.DELETE_SCHEDULE_SUCCESS,
  scheduleTypes.ADD_SCHEDULE_EXCEPTION_SUCCESS,
  scheduleTypes.UPDATE_SCHEDULE_EXCEPTION_SUCCESS,
  scheduleTypes.DELETE_SCHEDULE_EXCEPTION_SUCCESS,
]);

const entityKind = (collection) => (collection === 'incompleteTasks'
  ? 'tasks'
  : collection);
const entityKey = (collection, id) => `${entityKind(collection)}:${id}`;
const version = (item) => Math.max(
  new Date(item?.updatedDate || 0).getTime() || 0,
  new Date(item?.currentEventStatus?.createdDate || 0).getTime() || 0,
);
const scalarFields = [
  'name', 'description', 'startDate', 'endDate', 'completeDate', 'dueDate', 'archived', 'duration', 'rrule',
  'timezone', 'assignment', 'scheduleAssignment', 'advanceNotice', 'metadata', 'eventData', 'workflows', 'exceptionDate',
  'scheduleExceptionId',
];

// Search results expand relationships differently from save responses. Compare persisted values, not hydrated labels.
const savedValues = (item) => {
  const values = {};
  scalarFields.forEach((field) => { if (item[field] != null) values[field] = item[field]; });
  values.tags = (item.tags || []).map((tag) => Number(tag.id)).sort((left, right) => left - right);
  values.eventTemplateId = Number(item.eventTemplate?.id || item.eventTemplateId) || null;
  values.scheduleId = Number(item.schedule?.id || item.scheduleId) || null;
  values.parentScheduleId = Number(item.parentSchedule?.id) || null;
  values.statusId = Number(item.currentEventStatus?.id) || null;
  values.statusAssignment = item.currentEventStatus?.assignment || '';
  values.statusComment = item.currentEventStatus?.statusComment || '';
  return values;
};

const isObsoleteUpdate = (state, action) => {
  const task = action.task?.event || action.event;
  const item = task || action.schedule || action.scheduleException;
  if (!item?.id) return false;
  let collection = 'scheduleExceptions';
  if (task) collection = 'tasks';
  else if (action.schedule) collection = 'schedules';
  const pending = state.boardPendingChanges?.[entityKey(collection, item.id)];
  if (!pending) return false;
  if (!pending.item) return !pending.removedItem || version(item) <= version(pending.removedItem);
  return version(pending.item) > 0 && version(item) > 0 && version(item) < version(pending.item);
};

const indexItems = (state) => {
  const indexed = new Map();
  collections.forEach((collection) => {
    (state[collection] || []).forEach((item) => {
      const key = entityKey(collection, item.id);
      const existing = indexed.get(key);
      indexed.set(key, {
        item,
        collections: [...(existing?.collections || []), collection],
      });
    });
  });
  return indexed;
};

const reconcileSnapshot = (state, action) => {
  const pending = state.boardPendingChanges || {};
  if (!Object.keys(pending).length) return action;
  const snapshot = indexItems(action);
  const remaining = {};
  const result = { ...action };
  const overrides = {};
  Object.entries(pending).forEach(([key, change]) => {
    const fetched = snapshot.get(key);
    const requestIncludesChange = (state.boardRequestChangeSequence || 0) >= change.sequence;
    const acknowledged = change.item
      ? fetched && ((version(change.item) > 0 && version(fetched.item) > version(change.item))
        || isEqual(savedValues(fetched.item), savedValues(change.item)))
      : !fetched || (change.removedItem && version(change.removedItem) > 0 && version(fetched.item) > version(change.removedItem));
    if (requestIncludesChange && acknowledged) return;
    remaining[key] = change;
    collections.forEach((collection) => {
      if (entityKind(collection) !== key.split(':')[0]) return;
      const items = overrides[collection] || result[collection] || [];
      const withoutChanged = items.filter((item) => entityKey(collection, item.id) !== key);
      overrides[collection] = change.item && change.collections.includes(collection)
        ? [...withoutChanged, change.item]
        : withoutChanged;
    });
  });
  Object.assign(result, overrides);
  result.boardPendingChanges = remaining;
  if (overrides.schedules || overrides.scheduleExceptions) {
    result.events = generateEventsFromSchedules(
      result.schedules || [],
      result.scheduleExceptions || [],
      state.startDate,
      state.endDate,
      action.barnUsersMap || {},
      action.barnMetadata || {},
      action.mappedStatuses || {},
    );
  }
  return result;
};

const rememberChanges = (state, nextState, action) => {
  const before = indexItems(state);
  const after = indexItems(nextState);
  const keys = new Set([...before.keys(), ...after.keys()]);
  const changes = {};
  const sequence = (state.boardChangeSequence || 0) + 1;
  const changedTask = action.updatedEvent || action.task?.event || action.event;
  let changedItem = changedTask || action.updatedSchedule || action.schedule;
  let changedCollection = changedTask
    ? 'tasks'
    : 'schedules';
  if (action.newStatus) {
    changedCollection = 'tasks';
    changedItem = {
      id: action.eventId,
      updatedDate: action.newStatus.createdDate,
    };
  }
  keys.forEach((key) => {
    const previous = before.get(key);
    const current = after.get(key);
    if (isEqual(previous?.item, current?.item) && isEqual(previous?.collections, current?.collections)) return;
    changes[key] = {
      item: current?.item || null,
      removedItem: !current && changedItem && key === entityKey(changedCollection, changedItem.id)
        ? changedItem
        : null,
      collections: current?.collections || [],
      sequence,
    };
  });
  if (!Object.keys(changes).length) return nextState;
  return {
    ...nextState,
    boardChangeSequence: sequence,
    boardPendingChanges: {
      ...state.boardPendingChanges,
      ...changes,
    },
  };
};

export default (reduceBoard) => (state, action = {}) => {
  if (action.type === SELECT_BARN) return reduceBoard(undefined, {});
  if (!state) return reduceBoard(state, action);
  if (action.type === types.GET_BOARD_REQUESTED) {
    const sameScope = action.dataScopeKey && action.dataScopeKey === state.boardMutationScopeKey;
    return {
      ...reduceBoard(state, action),
      boardMutationScopeKey: action.dataScopeKey,
      boardBarnId: action.boardBarnId,
      boardTenant: action.boardTenant,
      boardFilters: action.boardFilters,
      boardBarnUsersMap: action.barnUsersMap,
      boardPendingChanges: sameScope
        ? state.boardPendingChanges
        : {},
      boardPendingCreations: sameScope
        ? state.boardPendingCreations
        : [],
      boardRequestChangeSequence: state.boardChangeSequence || 0,
    };
  }
  if (action.type === types.GET_BOARD_SUCCESS) {
    if (action.requestId != null && action.requestId !== state.boardRequestId) return state;
    if (action.dataScopeKey && state.boardMutationScopeKey && action.dataScopeKey !== state.boardMutationScopeKey) return state;
    const reconciled = reconcileSnapshot(state, action);
    return {
      ...reduceBoard(state, reconciled),
      boardPendingChanges: reconciled.boardPendingChanges || {},
      boardPendingCreations: getPendingCreatedSchedules(state.boardPendingCreations || [], reconciled.tasks || [], {
        ...action,
        startDate: state.startDate,
        endDate: state.endDate,
        scheduleExceptions: reconciled.scheduleExceptions || [],
      }),
    };
  }
  if (!mutationTypes.has(action.type)) return reduceBoard(state, action);
  if ((action.boardBarnId != null && String(action.boardBarnId) !== String(state.boardBarnId))
    || (action.boardTenant != null && action.boardTenant !== state.boardTenant)) return state;
  if (isObsoleteUpdate(state, action)) return state;
  let nextState = reduceBoard(state, action);
  if (nextState === state) return state;
  if (!state.boardDataScopeKey) return nextState;
  const schedules = (nextState.schedules || []).filter((schedule) => matchesBoardItem(schedule, state, 'schedules'));
  if (schedules.length !== (nextState.schedules || []).length) {
    nextState = {
      ...nextState,
      schedules,
      events: generateEventsFromSchedules(
        schedules,
        nextState.scheduleExceptions,
        state.startDate,
        state.endDate,
        action.barnUsersMap || state.boardBarnUsersMap || {},
        action.barnMetadata || {},
        action.mappedStatuses || {},
      ),
    };
  }
  const remembered = rememberChanges(state, nextState, action);
  const pendingCreations = (state.boardPendingCreations || [])
    .map((created) => schedules.find((schedule) => schedule.id === created.id)).filter(Boolean);
  if (action.type !== scheduleTypes.ADD_SCHEDULE_SUCCESS || !action.newSchedule?.id
    || !schedules.some((schedule) => schedule.id === action.newSchedule.id)) {
    return {
      ...remembered,
      boardPendingCreations: pendingCreations,
    };
  }
  return {
    ...remembered,
    boardPendingCreations: [...pendingCreations.filter((schedule) => schedule.id !== action.newSchedule.id),
      action.newSchedule],
  };
};
